import * as React from "react";
import { useMemo, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryGrayButton,
  Severity
} from "@velaro/velaro-component-lib";
import { useConditionPresets } from "@/providers/ConditionPresetsProvider";
import useToastAlert from "@/hooks/useToastAlert";
import { ExpressionEditor } from "@/components/Expressions/ExpressionEditor";
import {
  Expression,
  getExpressionLabel
} from "@/components/Expressions/types";
import { ConditionPreset } from "@/components/Expressions/conditionPresetTypes";

type DrawerState =
  | { mode: "closed" }
  | { mode: "new" }
  | { mode: "edit"; preset: ConditionPreset };

function safeParseExpression(json: string): Expression {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as Expression) : [];
  } catch {
    return [];
  }
}

function toShortDate(iso: string | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function ConditionPresetsPage() {
  const { presets, loading, create, update, remove } = useConditionPresets();
  const toast = useToastAlert();

  const [drawer, setDrawer] = useState<DrawerState>({ mode: "closed" });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [expression, setExpression] = useState<Expression>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const existingNames = useMemo(() => {
    const editingId = drawer.mode === "edit" ? drawer.preset.id : null;
    return new Set(
      presets
        .filter((p) => p.id !== editingId)
        .map((p) => (p.name || "").trim().toLowerCase())
    );
  }, [presets, drawer]);

  function openNew() {
    setName("");
    setDescription("");
    setExpression([]);
    setError(null);
    setDrawer({ mode: "new" });
  }

  function openEdit(preset: ConditionPreset) {
    setName(preset.name);
    setDescription(preset.description ?? "");
    setExpression(safeParseExpression(preset.expressionJson));
    setError(null);
    setDrawer({ mode: "edit", preset });
  }

  function closeDrawer() {
    setDrawer({ mode: "closed" });
    setError(null);
  }

  async function handleSave() {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    if (existingNames.has(trimmed.toLowerCase())) {
      setError(`A preset named '${trimmed}' already exists.`);
      return;
    }
    if (!expression || expression.length === 0) {
      setError("Add at least one condition before saving.");
      return;
    }
    if (expression.some((g) => g.some((c) => !c.type))) {
      setError("Finish configuring every condition before saving.");
      return;
    }

    setSaving(true);
    try {
      const req = {
        name: trimmed,
        description: description.trim() || undefined,
        expressionJson: JSON.stringify(expression)
      };
      const result =
        drawer.mode === "edit"
          ? await update(drawer.preset.id, req)
          : await create(req);
      if (!result) {
        setError("Server rejected the preset. Try a different name.");
        return;
      }
      toast.displayToast?.(
        Severity.Success,
        drawer.mode === "edit"
          ? `Updated preset '${result.name}'.`
          : `Saved preset '${result.name}'.`
      );
      closeDrawer();
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    if (pendingDeleteId == null) return;
    const victim = presets.find((p) => p.id === pendingDeleteId);
    const ok = await remove(pendingDeleteId);
    setPendingDeleteId(null);
    if (ok && victim) {
      toast.displayToast?.(
        Severity.Success,
        `Deleted preset '${victim.name}'. Workflows that inlined it keep their copy.`
      );
    } else if (!ok) {
      toast.displayToast?.(Severity.Error, "Failed to delete preset.");
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            Condition Presets
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Reusable condition groups that drop into any workflow's condition
            builder. Loading a preset inlines a copy — edits to the preset do
            not retroactively change existing workflows.
          </p>
        </div>
        <PrimaryBlueButton label="New preset" onClick={openNew} />
      </div>

      {loading && presets.length === 0 ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : presets.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-sm text-slate-500">
          No saved presets yet. Save a condition group from any workflow's
          condition builder to create one, or click <strong>New preset</strong>{" "}
          above.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">
                  Description
                </th>
                <th className="text-left px-3 py-2 font-medium">
                  Last Modified
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {presets.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{p.name}</div>
                    <div className="text-[11px] text-slate-400">
                      {getExpressionLabel(safeParseExpression(p.expressionJson))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {p.description || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {toShortDate(p.lastModified)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:underline mr-3"
                      onClick={() => openEdit(p)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-red-600 hover:underline"
                      onClick={() => setPendingDeleteId(p.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Create drawer as a wide modal */}
      <Modal show={drawer.mode !== "closed"} maxWidth="max-w-3xl">
        <ModalHeader
          title={
            drawer.mode === "edit" ? "Edit condition preset" : "New condition preset"
          }
          onClose={closeDrawer}
        />
        <ModalBody>
          <div className="m-4 flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-700">Name</span>
              <input
                className="border border-slate-300 rounded px-2 py-1 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                placeholder="e.g. VIP SMS after hours"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-700">
                Description (optional)
              </span>
              <textarea
                className="border border-slate-300 rounded px-2 py-1 text-sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                rows={2}
              />
            </label>
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">
                Conditions
              </div>
              <ExpressionEditor
                expressionType="condition-node"
                expression={expression}
                onUpdate={setExpression}
                hidePresetControls
              />
            </div>
            {error && <div className="text-xs text-red-600">{error}</div>}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="mx-4 my-2 flex justify-between items-center gap-2">
            <SecondaryGrayButton label="Cancel" onClick={closeDrawer} />
            <PrimaryBlueButton
              label={saving ? "Saving…" : "Save preset"}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <Modal show={pendingDeleteId != null}>
        <ModalHeader
          title="Delete preset?"
          onClose={() => setPendingDeleteId(null)}
        />
        <ModalBody>
          <div className="m-4 text-sm text-slate-600">
            Workflows that inlined this preset keep their copy. Deletion only
            removes the reusable template.
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="mx-4 my-2 flex justify-between items-center gap-2">
            <SecondaryGrayButton
              label="Cancel"
              onClick={() => setPendingDeleteId(null)}
            />
            <PrimaryBlueButton label="Delete" onClick={handleConfirmDelete} />
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}
